import appLogger from "../../logger/appLogger";
import { collectDefaultCapabilityKeyIssues, collectOverrideCapabilityKeyIssues, DEFAULT_CAPABILITIES, } from "@next-platform/packages-ts-types";
const capabilitiesLogger = appLogger.withTag("Capabilities");
/**
 * The default configuration and the capability types ship inside the same package, so a
 * mismatch means the running build is broken rather than merely out of date. It is reported
 * once per start because neither side can change while the application is running.
 */
let hasReportedDefaultConfigurationIssues = false;
/**
 * Contentful overrides change per application and per refresh, so unknown keys are reported
 * once per distinct resolved definition instead of once per start.
 */
let lastReportedOverrideSignature = null;
function reportDefaultConfigurationIssues(options) {
    if (hasReportedDefaultConfigurationIssues) {
        return;
    }
    hasReportedDefaultConfigurationIssues = true;
    const issues = collectDefaultCapabilityKeyIssues(DEFAULT_CAPABILITIES);
    if (issues.length === 0) {
        return;
    }
    capabilitiesLogger.warn("Default application capabilities do not match the capability types", {
        issues,
        applicationId: options.applicationId,
        platform: options.platform,
    });
}
function readCapabilityKeyPaths(capabilities) {
    return Object.entries(capabilities).flatMap(([capabilityKey, capability]) => [capabilityKey, ...Object.keys(capability?.subCapabilities ?? {})]);
}
function reportOverrideIssues(options) {
    const capabilities = options.applicationDefinition?.capabilities;
    if (!capabilities) {
        return;
    }
    const signature = `${options.applicationId}:${options.platform}:${readCapabilityKeyPaths(capabilities).join(",")}`;
    if (signature === lastReportedOverrideSignature) {
        return;
    }
    lastReportedOverrideSignature = signature;
    const issues = collectOverrideCapabilityKeyIssues({ capabilities, defaultCapabilitiesConfig: DEFAULT_CAPABILITIES });
    if (issues.length === 0) {
        return;
    }
    capabilitiesLogger.info("Application capabilities contain keys this build does not know about", {
        issues,
        applicationId: options.applicationId,
        platform: options.platform,
    });
}
/**
 * Reports capability keys that do not line up with the capability types, at two severities:
 * a warning for the default configuration, which must always match, and an informational entry
 * for Contentful overrides, where forward-compatible unknown keys are expected.
 *
 * Unknown keys stay inert either way — this never throws and never changes capability resolution.
 */
export default function validateApplicationCapabilityKeys(options) {
    try {
        reportDefaultConfigurationIssues(options);
        reportOverrideIssues(options);
    }
    catch (error) {
        capabilitiesLogger.debug("Capability key validation failed", error);
    }
}
/**
 * Clears the deduplication state so the next call reports again. Reporting is deduplicated for the
 * lifetime of the module, so only tests, which simulate repeated application starts, need this.
 */
export function resetApplicationCapabilityKeyValidation() {
    hasReportedDefaultConfigurationIssues = false;
    lastReportedOverrideSignature = null;
}
